import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Bot, Zap, Gauge, Shield, TrendingUp, Info, Rocket, KeyRound, Star } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { clsx } from "../lib/format";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import BotInstanceCard from "../components/BotInstanceCard";

const riskColor = (r) => (r === "Low" ? "text-pos bg-pos/15" : r === "High" ? "text-neg bg-neg/15" : "text-gold bg-gold/15");
const vendorSlug = (name) => (name || "").trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

function BotLogo({ bot, className = "h-12 w-12", iconClass = "h-6 w-6" }) {
  if (bot?.logo_url) return <img src={bot.logo_url} alt={bot.name} className={clsx(className, "rounded-2xl object-cover border border-white/10 bg-fx-base")} />;
  return <div className={clsx(className, "rounded-2xl bg-gradient-to-br from-brandblue/25 to-brandblue/5 border border-brandblue/20 grid place-items-center text-brandblue")}><Bot className={iconClass} /></div>;
}

function Info2({ icon: Icon, label, value }) {
  return (
    <div className="rounded-lg bg-fx-base border border-white/5 p-3">
      <div className="text-xs text-fx-s3 flex items-center gap-1"><Icon className="h-3.5 w-3.5" /> {label}</div>
      <div className="font-mono font-semibold mt-1">{value}</div>
    </div>
  );
}

export default function Bots() {
  const navigate = useNavigate();
  const [bots, setBots] = useState([]);
  const [instances, setInstances] = useState([]);
  const [detail, setDetail] = useState(null);
  const [activate, setActivate] = useState(null); // bot being activated
  const [licenseKey, setLicenseKey] = useState("");
  const [tab, setTab] = useState("marketplace");

  const loadBots = useCallback(() => api.get("/bots/store").then((r) => setBots(r.data)), []);
  const loadInstances = useCallback(async () => {
    try { const { data } = await api.get("/bots/instances"); setInstances(data); } catch {}
  }, []);

  useEffect(() => { loadBots(); loadInstances(); }, [loadBots, loadInstances]);
  useEffect(() => {
    const t = setInterval(loadInstances, 2500);
    return () => clearInterval(t);
  }, [loadInstances]);

  const openActivate = (bot) => { setActivate(bot); setLicenseKey(""); };

  const submitActivate = async () => {
    try {
      await api.post(`/bots/${activate.id}/install`, { activation_key: licenseKey });
      toast.success(`${activate.name} activated — see My Trading Bots`);
      setActivate(null);
      setTab("active");
      loadBots(); loadInstances();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Invalid activation key");
    }
  };

  const runningCount = instances.filter((i) => i.status === "running").length;

  return (
    <div className="p-4 lg:p-8 space-y-6 max-w-[1400px]">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">Featured Trading Bots</h1>
          <p className="text-fx-s3 mt-1">A curated marketplace of AI trading strategies from leading vendors. Activate one, test it on your demo wallet, then switch to live when you are ready.</p>
        </div>
        <Badge className="bg-brandblue/15 text-brandblue border-brandblue/30 gap-1"><Zap className="h-3 w-3" /> {runningCount} running</Badge>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-fx-s1 border border-white/5">
          <TabsTrigger value="marketplace" data-testid="tab-marketplace" className="data-[state=active]:bg-brandblue data-[state=active]:text-white">Marketplace</TabsTrigger>
          <TabsTrigger value="active" data-testid="tab-active" className="data-[state=active]:bg-brandblue data-[state=active]:text-white">My Active Bots ({instances.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="marketplace" className="mt-6">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {bots.map((b) => (
              <div key={b.id} data-testid={`bot-card-${b.id}`} className="rounded-xl bg-fx-s1 border border-white/5 p-6 flex flex-col hover:border-brandblue/40 transition-colors relative">
                {b.featured && <span data-testid={`bot-featured-${b.id}`} className="absolute top-3 right-3 flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-gold/15 text-gold border border-gold/30"><Star className="h-3 w-3 fill-gold" /> Featured</span>}
                <div className="flex items-start gap-4">
                  <BotLogo bot={b} className="h-16 w-16 shrink-0" iconClass="h-8 w-8" />
                  <div className="min-w-0 flex-1">
                    <h3 className="font-display font-bold text-lg leading-tight truncate">{b.name}</h3>
                    {b.vendor ? (
                      <button data-testid={`bot-vendor-${b.id}`} onClick={() => navigate(`/vendors/${vendorSlug(b.vendor)}`)} className="text-xs text-brandblue font-medium mt-0.5 truncate hover:underline text-left">Powered by: {b.vendor}</button>
                    ) : (
                      <div className="text-xs text-fx-s3 font-mono mt-0.5">v{b.version} · {b.category}</div>
                    )}
                    <span className={clsx("inline-block mt-1.5 px-2 py-0.5 rounded text-[11px] font-mono", riskColor(b.risk_level))}>{b.risk_level} risk</span>
                  </div>
                </div>
                <p className="text-sm text-fx-s3 mt-3 flex-1">{b.short_description || b.description}</p>
                {b.performance && (
                  <div data-testid={`bot-perf-${b.id}`} className="mt-3 rounded-lg bg-fx-base border border-white/5 px-3 py-2">
                    {b.performance.has_data ? (
                      <>
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-fx-s3">Win rate</span>
                          <span className={clsx("font-mono font-semibold", b.performance.win_rate >= 50 ? "text-pos" : "text-neg")}>{b.performance.win_rate}%</span>
                        </div>
                        <div className="flex items-center justify-between text-xs mt-1">
                          <span className="text-fx-s3">ROI · {b.performance.total_trades} trades</span>
                          <span className={clsx("font-mono font-semibold", b.performance.roi_pct >= 0 ? "text-pos" : "text-neg")}>{b.performance.roi_pct >= 0 ? "+" : ""}{b.performance.roi_pct}%</span>
                        </div>
                        {b.performance.recent.length > 0 && (
                          <div className="flex items-center gap-1 mt-2">
                            <span className="text-[10px] text-fx-s3 mr-1">Recent</span>
                            {b.performance.recent.map((r, i) => (
                              <span key={i} className={clsx("h-2 w-2 rounded-full", r === "win" ? "bg-pos" : "bg-neg")} title={r} />
                            ))}
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="flex items-center gap-2 text-xs text-fx-s3"><TrendingUp className="h-3.5 w-3.5" /> No live performance data yet</div>
                    )}
                  </div>
                )}
                <div className="grid grid-cols-2 gap-3 mt-4 text-xs">
                  <div><div className="text-fx-s3">Daily target</div><div className="font-mono text-pos">+{b.daily_target}%</div></div>
                  <div><div className="text-fx-s3">Max drawdown</div><div className="font-mono text-neg">-{b.max_drawdown}%</div></div>
                  <div><div className="text-fx-s3">Min capital</div><div className="font-mono">${b.min_capital}</div></div>
                  <div><div className="text-fx-s3">License</div><div className="font-mono">${b.price}/{b.license_days}d</div></div>
                </div>
                <div className="flex gap-2 mt-5">
                  {b.installed ? (
                    <Button disabled data-testid={`bot-installed-${b.id}`} className="flex-1 bg-fx-s2 text-pos gap-2 opacity-90"><Rocket className="h-4 w-4" /> Installed</Button>
                  ) : (
                    <Button onClick={() => openActivate(b)} data-testid={`bot-activate-${b.id}`} className="flex-1 bg-brandblue hover:bg-blue-500 gap-2"><KeyRound className="h-4 w-4" /> Activate</Button>
                  )}
                  <Button onClick={() => setDetail(b)} data-testid={`bot-details-${b.id}`} variant="outline" className="border-white/10 bg-fx-base"><Info className="h-4 w-4" /></Button>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="active" className="mt-6">
          {instances.length === 0 ? (
            <div className="rounded-xl bg-fx-s1 border border-white/5 p-12 text-center">
              <Bot className="h-10 w-10 text-fx-s3 mx-auto" />
              <p className="text-fx-s3 mt-3">No active bots yet. Head to the Marketplace and test one in demo mode.</p>
              <Button onClick={() => setTab("marketplace")} className="mt-4 bg-brandblue hover:bg-blue-500">Browse bots</Button>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-6">
              {instances.map((inst) => <BotInstanceCard key={inst.id} inst={inst} onChanged={loadInstances} />)}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Details dialog */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="bg-fx-s2 border-white/10 text-white max-w-lg" data-testid="bot-detail-dialog">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display flex items-center gap-3">
                  <BotLogo bot={detail} className="h-14 w-14" iconClass="h-7 w-7" />
                  <div>
                    <div>{detail.name}</div>
                    {detail.vendor && <div className="text-xs text-brandblue font-medium">Powered by: {detail.vendor}</div>}
                  </div>
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 text-sm">
                <p className="text-fx-s3">{detail.description}</p>
                <div className="rounded-lg bg-fx-base border border-white/5 p-3">
                  <div className="text-xs text-brandblue font-mono uppercase tracking-wider mb-1 flex items-center gap-1"><Zap className="h-3.5 w-3.5" /> Strategy</div>
                  <p className="text-fx-s3">{detail.strategy}</p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Info2 icon={Gauge} label="Frequency" value={detail.frequency} />
                  <Info2 icon={Shield} label="Max trades/day" value={detail.max_trades_per_day} />
                  <Info2 icon={TrendingUp} label="Trade interval" value={`${detail.trade_interval}s`} />
                  <Info2 icon={Gauge} label="Capital range" value={`$${detail.min_capital}–$${detail.max_capital}`} />
                </div>
                <div>
                  <div className="text-xs text-fx-s3 mb-2">Supported assets</div>
                  <div className="flex flex-wrap gap-1.5">
                    {detail.supported_assets?.map((a) => <span key={a} className="px-2 py-0.5 rounded bg-fx-base border border-white/10 text-xs font-mono">{a}</span>)}
                  </div>
                </div>
                <Button onClick={() => { openActivate(detail); setDetail(null); }} className="w-full bg-brandblue hover:bg-blue-500 gap-2"><KeyRound className="h-4 w-4" /> Activate with key</Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Activate (license key) dialog */}
      <Dialog open={!!activate} onOpenChange={(o) => !o && setActivate(null)}>
        <DialogContent className="bg-fx-s2 border-white/10 text-white" data-testid="activate-dialog">
          <DialogHeader><DialogTitle className="font-display flex items-center gap-3"><BotLogo bot={activate} className="h-14 w-14" iconClass="h-7 w-7" /> Activate {activate?.name}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Bot Activation Key</Label>
              <Input data-testid="activate-key-input" value={licenseKey} onChange={(e) => setLicenseKey(e.target.value)} placeholder="FXCLOUD-XXXX-XXXX-XXXX" className="bg-fx-base border-white/10 font-mono tracking-widest" />
            </div>
            <div className="rounded-lg bg-fx-base border border-brandblue/30 p-4 text-sm text-white leading-relaxed" data-testid="activation-instructions">
              Enter the activation key issued by the bot administrator or authorized bot provider. Once the key is successfully verified, the bot will be added to My Trading Bots, where you can activate and run it in Demo Mode using virtual funds before using it for Live Trading.
            </div>
          </div>
          <DialogFooter>
            <Button onClick={submitActivate} data-testid="activate-confirm-btn" className="bg-brandblue hover:bg-blue-500 gap-2"><KeyRound className="h-4 w-4" /> Activate bot</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
